import { Component, Prop, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    ContentNotificationTypesGrid,
    initialNotificationTypesGridCrudModelBase,
    NotificationTypesGridService,
    NotificationTypesGridState
} from '@/modules/configurationModule/detail/notifications/components/notification-types-grid/notification-types-grid.models'
import NotificationTypesModal
    from '@/modules/configurationModule/detail/notifications/components/notification-types-modal/notification-types-modal.vue'
import { PaginatorLaboratoryDatasource } from '@/shared/resources/datasources/paginator/paginator-laboratory-datasource'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { NotificationTypesModalState } from '@/modules/configurationModule/detail/notifications/components/notification-types-modal/notification-types-modal.models'
import {
    CreateNotificationTypeRequest,
    PatchNotificationTypeRequest
} from '@/modules/configurationModule/detail/notifications/models/notification-types'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { TranslationRequest, TranslationResponse } from '@/api/services/common.dto'
import { notificationTypeServices } from '@/modules/configurationModule/detail/notifications/services/notification-type-services'
import { StrictNullable } from '@/shared/types/app.types'
import { notificationTypesGridColumnDefs } from '@/modules/configurationModule/detail/notifications/components/notification-types-grid/notification-types-grid.column-defs'

@Component({
    components: {
        NotificationTypesModal,
        ConfigurationSectionHeader,
        VtGrid
    }
})
export default class NotificationTypesGrid extends BaseCrudServerSideComponent<ContentNotificationTypesGrid> implements BaseCrud {
    @Ref() readonly notificationTypesModalWrapper!: any
    @Ref() readonly notificationTypesModal!: any

    @Prop() readonly isGlobal!: boolean
    @Prop() readonly serviceInstance!: () => Promise<NotificationTypesGridService>

    state: NotificationTypesGridState
    constructor() {
        super()
        this.state = initialNotificationTypesGridCrudModelBase()
    }

    async created() {
        const notificationTypes =
            await this.$_toCallSecure(notificationTypeServices.getAllNotificationTypes(this.isGlobal)) ?? []
        this.state.columnsDef = notificationTypesGridColumnDefs(notificationTypes)
    }

    async mounted() {
        const customService: NotificationTypesGridService = await this.serviceInstance()
        this.state.datasource = new PaginatorLaboratoryDatasource(customService, this.laboratoryId)
        this.state.service = customService
        this.state.gridUiComponent =
            this.isGlobal
                ? 'uicomponent.configuration.global.notifications.notificationsTypesGrid'
                : 'uicomponent.configuration.local.notifications.notificationsTypesGrid'
    }
    get laboratoryId(): number {
        return this.$store.getters.labId
    }
    showNotificationTypesModal(title: string | any, selectedNotification: ContentNotificationTypesGrid): void {
        this.state.actionTitle = title
        this.notificationTypesModalWrapper.show([selectedNotification])
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected: StrictNullable<RowNode> = params?.node
                    const selectedRow: ContentNotificationTypesGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.notificationTypesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            }
        ]
    }
    mapTranslation(translation: Partial<TranslationResponse>): TranslationRequest {
        return {
            languageId: translation.language!.id,
            notes: translation.notes,
            value: translation.value!
        }
    }
    async createOperation(data: NotificationTypesModalState): Promise<void> {
        if (!this.state.service) {
            return
        }
        const notificationTypeToCreate: CreateNotificationTypeRequest = {
            emailChannelActive: data.basicData?.emailChannelActive,
            htmlBodyTranslations: data.htmlBodyTranslations?.map(translation => this.mapTranslation(translation)) ?? [],
            inboxChannelActive: data.basicData?.inboxChannelActive,
            notificationType: data.basicData?.notificationType?.code,
            plainBodyTranslations: data.plainBodyTranslations?.map(translation => this.mapTranslation(translation)) ?? [],
            smsChannelActive: data.basicData?.smsChannelActive,
            subjectTranslations: data.subjectTranslations?.map(translation => this.mapTranslation(translation)) ?? []
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(this.state.service.createGlobalNotification(notificationTypeToCreate, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.notificationTypesModal.initializeNotification(response)
        this.reloadData()
    }

    async patchOperation(data: NotificationTypesModalState): Promise<void> {
        const globalNotificationToPatch: PatchNotificationTypeRequest = {
            id: data.id,
            emailChannelActive: data.basicData?.emailChannelActive,
            htmlBodyTranslations: data.htmlBodyTranslations.map(translation => this.mapTranslation(translation)),
            inboxChannelActive: data.basicData?.inboxChannelActive,
            notificationType: data.basicData?.notificationType?.code,
            plainBodyTranslations: data.plainBodyTranslations?.map(translation => this.mapTranslation(translation)),
            smsChannelActive: data.basicData?.smsChannelActive,
            subjectTranslations: data.subjectTranslations?.map(translation => this.mapTranslation(translation))
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall((this.state.service as any).patchGlobalNotificationBatch([globalNotificationToPatch], this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteBatchOperation(globalNotifications: ContentNotificationTypesGrid[]): Promise<void> {
        if (!this.state.service) {
            return
        }

        const sampleProcessesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: globalNotifications.map((globalNotification: ContentNotificationTypesGrid) => Number(globalNotification?.id))
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(this.state.service.deleteGlobalNotificationBatch(sampleProcessesToDelete, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        if (globalNotifications.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: globalNotifications?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(globalNotifications: ContentNotificationTypesGrid[]): Promise<void> {
        if (globalNotifications.length) {
            const selectedRowsSize: number = this.state?.selectedRows?.length
            const labelConfirmation: string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion: string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction: string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this.deleteBatchOperation(globalNotifications)
            }
        }
    }
}
